import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { profileApi } from '@/lib/api';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/Icons';
import LoadingSpinner from '@/components/LoadingSpinner';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const handleSaveName = async () => {
    if (!displayName.trim()) return;
    
    setSaving(true);
    
    const res = await profileApi.update({ display_name: displayName.trim() });
    
    if (res.success && res.data) {
      updateUser({ display_name: res.data.display_name });
      showToast('Имя сохранено', 'success');
    } else {
      showToast(res.error || 'Не удалось сохранить', 'error');
    }
    
    setSaving(false);
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Выберите изображение', 'error');
      return;
    }

    // Validate file size (500KB max for base64)
    if (file.size > 500 * 1024) {
      showToast('Изображение слишком большое. Максимум 500KB', 'error');
      return;
    }

    setUploadingPhoto(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        
        const res = await profileApi.update({ avatar_base64: base64 });
        
        if (res.success && res.data) {
          updateUser({ avatar_base64: res.data.avatar_base64 });
          showToast('Фото обновлено', 'success');
        } else {
          showToast(res.error || 'Не удалось загрузить фото', 'error');
        }
        
        setUploadingPhoto(false);
      };
      reader.onerror = () => {
        showToast('Ошибка чтения файла', 'error');
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch {
      showToast('Ошибка загрузки', 'error');
      setUploadingPhoto(false);
    }

    // Reset input
    e.target.value = '';
  };

  const handleRemovePhoto = async () => {
    if (!confirm('Удалить фото профиля?')) return;
    
    setUploadingPhoto(true);
    
    const res = await profileApi.update({ avatar_base64: '' });
    
    if (res.success) {
      updateUser({ avatar_base64: undefined });
      showToast('Фото удалено', 'success');
    } else {
      showToast(res.error || 'Не удалось удалить фото', 'error');
    }
    
    setUploadingPhoto(false);
  };

  const handleLogout = async () => {
    if (!confirm('Выйти из аккаунта?')) return;
    await logout();
    navigate('/login');
  };

  const getInitial = () => {
    if (user?.display_name) {
      return user.display_name.charAt(0).toUpperCase();
    }
    if (user?.login) {
      return user.login.charAt(0).toUpperCase();
    }
    return '?';
  };

  if (!user) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <header className="page-header flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <h1 className="page-title ml-2">Профиль</h1>
      </header>

      <div className="p-4 space-y-6">
        {/* Avatar Section */}
        <section className="flex flex-col items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="hidden"
          />
          
          <button
            onClick={handlePhotoClick}
            disabled={uploadingPhoto}
            className="relative w-24 h-24 rounded-full overflow-hidden bg-primary-100 flex items-center justify-center text-primary-600 text-3xl font-bold hover:ring-4 hover:ring-primary-200 transition-all"
          >
            {uploadingPhoto ? (
              <LoadingSpinner size="lg" />
            ) : user.avatar_base64 ? (
              <img
                src={user.avatar_base64}
                alt="Avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              getInitial()
            )}
          </button>
          
          <div className="mt-3 flex gap-2">
            <button
              onClick={handlePhotoClick}
              disabled={uploadingPhoto}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              {user.avatar_base64 ? 'Изменить фото' : 'Добавить фото'}
            </button>
            {user.avatar_base64 && (
              <>
                <span className="text-gray-300">|</span>
                <button
                  onClick={handleRemovePhoto}
                  disabled={uploadingPhoto}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Удалить
                </button>
              </>
            )}
          </div>
        </section>

        {/* Name Section */}
        <section>
          <h2 className="section-title">Имя</h2>
          <div className="card p-4">
            <div className="flex gap-3 items-center">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Введите имя"
                className="input flex-1"
              />
              <button
                onClick={handleSaveName}
                disabled={saving || !displayName.trim() || displayName.trim() === user.display_name}
                className="w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <LoadingSpinner size="sm" className="text-white" />
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Body Measurements Section */}
        <section>
          <h2 className="section-title">Параметры тела</h2>
          <Link
            to="/measurements"
            className="card p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">Замеры тела</p>
                <p className="text-sm text-gray-500">Вес, грудь, талия и другие</p>
              </div>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
          </Link>
        </section>

        {/* Admin Section - Only for admins */}
        {user.role === 'admin' && (
          <section>
            <h2 className="section-title">Администрирование</h2>
            <Link
              to="/admin"
              className="card p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Панель администратора</p>
                  <p className="text-sm text-gray-500">Управление пользователями</p>
                </div>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400" />
            </Link>
          </section>
        )}

        {/* Logout Section */}
        <section>
          <button
            onClick={handleLogout}
            className="w-full card p-4 flex items-center justify-center gap-2 text-red-600 hover:bg-red-50 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
            </svg>
            Выйти из аккаунта
          </button>
        </section>
      </div>
    </div>
  );
}
